import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { liveQuery } from 'dexie';
import { BehaviorSubject, from } from 'rxjs';
import { DeviceService } from '../class/DeviceService';
import EntityManager from '../database/EntityManager';
import { Unit } from '../database/models';
import { AccountStateService } from './account-state.service';
import { DeviceStateService } from './device-state.service';
import DriversService from './drivers.service';
import LandmarksService from './landmarks.service';

const SEARCHED_FIELD = ['tag'] as const;

@Injectable({
  providedIn: 'root',
})
class UnitsService extends EntityManager<Unit, 'service'> {
  private _deviceStateService = inject(DeviceStateService);
  private _landmarksService = inject(LandmarksService);
  private _accountStateService = inject(AccountStateService);
  private _driversService = inject(DriversService);
  public defaultGetAll = () =>
    this.table.orderBy('tag').toArray(units => units.filter(this._filterNonCompliantUnit));

  public values$ = from(
    liveQuery(async () => {
      const units = await this.defaultGetAll();

      this.allUnits$.next(units);

      return units;
    })
  );

  public $values = toSignal(this.values$);
  public allUnits$ = new BehaviorSubject<Unit[]>(this.$values());

  /** Units indexed by uid — the storageService.devices.services replacement for plain
   * label/uid_parent lookups (breadcrumbs, form selects, ...). Derived straight from
   * $values, so it excludes the synthetic root node (uid 0) $serviceTree carries. */
  public $unitsById = computed<Record<number, Unit>>(() => {
    const units = this.$values();
    if (!units) return undefined;

    const unitsById: Record<number, Unit> = {};
    units.forEach(unit => (unitsById[unit.uid] = unit));
    return unitsById;
  });

  /**
   * uid -> effective parent uid, with root-orphan reattachment applied (see
   * _getReattachmentContext): a top-level unit reports 0 even when it's really a fallback
   * root for a restricted user rather than a true uid_parent === 0. Companion to
   * $unitsById for consumers that need the corrected hierarchy (e.g. "go up one level"
   * navigation) without depending on the stats-only $serviceTree.
   */
  public $parentById = computed<Record<number, number>>(() => {
    const units = this.$values();
    if (!units) return undefined;

    const { reattachToRoot, rootUnitIds } = this._getReattachmentContext(units);
    // 0 (the synthetic root, mirrors DeviceService's default uid_parent) maps to itself.
    const parentById: Record<number, number> = { 0: 0 };
    units.forEach(unit => {
      parentById[unit.uid] = reattachToRoot && rootUnitIds.has(unit.uid) ? 0 : unit.uid_parent || 0;
    });
    return parentById;
  });

  /**
   * Service/unit tree (keyed by uid, including the synthetic root at 0), used only by
   * devices-stats.service and deviceedit.service for stats aggregation and the device-
   * move mutation. Backed by a BehaviorSubject rather than a signal(): those two services
   * still mutate the DeviceService nodes in place (see legacyProcessServices). Other
   * consumers should read $unitsById or $values instead.
   */
  private _serviceTree$ = new BehaviorSubject<Record<number, DeviceService>>(null);
  public $serviceTree = toSignal(this._serviceTree$);

  public clear(): void {
    this._serviceTree$.next(null);
  }

  constructor() {
    super({ name: 'service', model: Unit }); // 6 minutes (% 20 of location api)
  }

  public async getBySearch(query: string) {
    return this.table
      .orderBy('tag')
      .filter(user => {
        const lowerCaseValue = query.toLowerCase();
        return SEARCHED_FIELD.some(field => user[field]?.toLowerCase().includes(lowerCaseValue));
      })
      .toArray();
  }

  public async isEmpty(id: Unit['uid']) {
    const units = await this._hasNoSubFolders(id);
    const data = {
      pois: this._hasNoPois(id),
      drivers: this._hasNoDrivers(id),
      devices: this._hasNoDevices(id),
      units: units,
    };
    const all = Object.values(data).reduce((acc, num) => acc + Number(num), 0);
    return { ...data, all: all };
  }

  public legacyProcessServices(units: Unit[]) {
    const serviceTree: Record<number, DeviceService> = {};
    const { rootUnits, reattachToRoot, rootUnitIds } = this._getReattachmentContext(units);

    units.forEach(unit => {
      const storageUnit = new DeviceService(unit.uid, 'service', unit.label, unit.tag);
      const subDevices = this.getDevices(unit.uid, { directChildrenOnly: true });
      const subServices = this.getSubUnits(unit.uid, { directChildrenOnly: true }, units);

      storageUnit.devices = subDevices.map(({ uid }) => uid);
      storageUnit.childrenDevices = subDevices;
      storageUnit.services = subServices.map(({ uid }) => uid);
      storageUnit.childrenServices = subServices;
      storageUnit.uid_parent = reattachToRoot && rootUnitIds.has(unit.uid) ? 0 : unit.uid_parent || 0;
      storageUnit.totalDevice = subDevices.length;

      serviceTree[unit.uid] = storageUnit;
    });

    // add rootUnit here and not into unitsService to not disturb the values signal of units.
    const rootUnit = new DeviceService(0, 'Root', undefined, undefined);
    // take only subUnits to feed root unit
    rootUnit.services = rootUnits.map(({ uid }) => uid);

    serviceTree[0] = rootUnit;

    this._serviceTree$.next(serviceTree);
  }

  /**
   * Units to attach to the root.
   * Normally the true root units (uid_parent === 0). But a user with a restricted
   * view may not see any of those, so we fall back to the units that are highest
   * in the tree among what the user can see (their parent is not in their visible units).
   */
  public getRootUnits(units: Unit[] = this.$values() ?? []): Unit[] {
    const trueRootUnits = units.filter(unit => unit.uid_parent === 0);
    if (trueRootUnits.length) return trueRootUnits;

    const visibleIds = new Set(units.map(({ uid }) => uid));
    return units.filter(unit => !visibleIds.has(unit.uid_parent));
  }

  /**
   * The tree is not continuous when there is no true root unit (uid_parent === 0):
   * getRootUnits then falls back to the highest visible units, whose uid_parent points
   * to a unit the user cannot see. For a plain user (neither administrator nor supervisor)
   * we reattach these first child services to the root so the tree stays connected.
   */
  private _getReattachmentContext(units: Unit[]) {
    const rootUnits = this.getRootUnits(units);
    const account = this._accountStateService.$account();
    const isPlainUser = !!account && !account.administrator && !account.supervisor;
    const isDiscontinuousTree = !units.some(unit => unit.uid_parent === 0);
    const reattachToRoot = isPlainUser && isDiscontinuousTree;
    const rootUnitIds = new Set(rootUnits.map(({ uid }) => uid));
    return { rootUnits, reattachToRoot, rootUnitIds };
  }

  private _filterNonCompliantUnit(unit: Unit) {
    // ensure that unit is not buggy and compliant
    return unit.uid !== unit.uid_parent;
  }

  private _hasNoDevices(id: Unit['uid']) {
    const devices = Object.values(this._deviceStateService.$devicesMap() ?? {});
    return devices.reduce((acc, item) => acc + (item.uid_parent === id ? 1 : 0), 0);
  }

  private async _hasNoSubFolders(id: Unit['uid']) {
    const units = await this.getAll();
    return units.reduce((acc, item) => acc + (item.uid_parent === id ? 1 : 0), 0);
  }

  private _hasNoDrivers(id: Unit['uid']) {
    const driverArray = this._driversService?.$values();
    if (!driverArray.length) return 0;
    return driverArray.reduce((acc, item) => acc + (item.uid_service === id ? 1 : 0), 0);
  }

  private _hasNoPois(id: Unit['uid']): number {
    if (!this._landmarksService.$landmarksById()) return 0;
    const poiArray = Object.values(this._landmarksService.$landmarksById());
    return poiArray.reduce((acc, item) => acc + (item.uid_service === id ? 1 : 0), 0);
  }

  /**
   * Get all sub units of a unit target.
   * @param parentId - current parent id
   * @param options - directChildrenOnly is to return only children on first loop.
   * @param units - current units
   * @returns
   */
  public getSubUnits(
    parentId: number,
    options: { directChildrenOnly: boolean } = { directChildrenOnly: false },
    units: Unit[] = this.$values() ?? []
  ): Unit[] {
    if (parentId === null) return [];

    const children = units.filter(node => node.uid_parent === parentId);

    if (options.directChildrenOnly) return children;

    return children.flatMap(node => [node, ...this.getSubUnits(node.uid, options, units)]);
  }

  public getDevices(
    id: Unit['uid'],
    options: { directChildrenOnly: boolean } = { directChildrenOnly: false }
  ) {
    const devicesMap = this._deviceStateService.$devicesMap();
    const devices = devicesMap ? Object.values(devicesMap) : [];

    if (options.directChildrenOnly) {
      return devices.filter(device => device.uid_parent === id);
    }

    const allSubUnitsIds = [id, ...this.getSubUnits(id).map(({ uid }) => uid)];

    return devices.filter(device => allSubUnitsIds.includes(device.uid_parent));
  }
}

export default UnitsService;
